import apiAuth from '@/api/auth'
import {getItem, setItem} from '@/helpers/persistanceStorage'
import {currentSchoolId, currentSchoolType, nameToken} from '@/helpers/vars'
import {
  equals,
  find,
  pathOr,
  pipe,
  prop,
  propEq,
  propOr,
  not,
  sortBy,
  where,
} from 'ramda'
import {getIsAdmin} from '@/helpers/utils'

const state = {
  isSubmitting: false,
  isLoading: false,
  currentUser: null,
  validationErrors: null,
  isLoggedIn: null,
  schoolId: null,
  schoolType: null,
  currentSchool: null,
}

export const mutationTypes = {
  registerStart: '[auth] registerStart',
  registerSuccess: '[auth] registerSuccess',
  registerFailure: '[auth] registerFailure',

  loginStart: '[auth] loginStart',
  loginSuccess: '[auth] loginSuccess',
  loginFailure: '[auth] loginFailure',

  getCurrentUserStart: '[auth] getCurrentUserStart',
  getCurrentUserSuccess: '[auth] getCurrentUserSuccess',
  getCurrentUserFailure: '[auth] getCurrentUserFailure',

  updateCurrentUserStart: '[auth] updateCurrentUserStart',
  updateCurrentUserSuccess: '[auth] updateCurrentUserSuccess',
  updateCurrentUserFailure: '[auth] updateCurrentUserFailure',

  resetPasswordStart: '[auth] resetPasswordStart',
  resetPasswordSuccess: '[auth] resetPasswordSuccess',
  resetPasswordFailure: '[auth] resetPasswordFailure',

  getCurrentSchoolId: '[auth] getCurrentSchoolId',
  setCurrentSchoolId: '[auth] setCurrentSchoolId',

  setCurrentSchool: '[auth] setCurrentSchool',

  logout: '[auth] logout',
}

export const actionType = {
  register: '[auth] register',
  login: '[auth] login',
  getCurrentUser: '[auth] getCurrentUser',
  updateCurrentUser: '[auth] updateCurrentUser',
  logout: '[auth] logout',
  resetPassword: '[auth] resetPassword',
  setCurrentSchoolId: '[auth] setCurrentSchoolId',
}

export const getterTypes = {
  currentUser: '[auth] currentUser',
  isLoggedId: '[auth] isLoggedIn',
  isAnonymous: '[auth] isAnonymous',
  showList: '[auth] showList',
  currentSchoolId: '[auth] currentSchoolId',
  currentSchoolType: '[auth] currentSchoolType',
  hasPersonalPage: '[auth] hasPersonalPage',
  hasEnrollmentApplicationPage: '[auth] hasEnrollmentApplicationPage',
  hasTrainersPage: '[auth] hasTrainersPage',
  canDecideEnrollment: '[auth] canDecideEnrollment',
  currentSchoolKindsSport: '[auth] currentSchoolKindsSport',
  isAdmin: '[auth] is Admin',
}

const getters = {
  [getterTypes.currentUser]: (state) => state.currentUser,
  [getterTypes.isLoggedId]: (state) => Boolean(state.isLoggedIn),
  [getterTypes.isAnonymous]: (state) => state.isLoggedIn === false,
  [getterTypes.isAdmin]: (state) => getIsAdmin(state.currentUser),
  [getterTypes.showList]: (state) => (name) => {
    const pages = state.currentUser.role.pages
    if (pages) {
      const link = find(where({name: equals(name)}), pages)
      if (link) {
        return link.value
      }
    }

    return false
  },
  [getterTypes.currentSchoolId]: (state) => {
    const schoolId = state.schoolId || getItem(currentSchoolId)
    state.schoolType = getItem(currentSchoolType)

    if (schoolId) {
      if (schoolId === 'all') {
        return schoolId
      }
      return Number(schoolId)
    }
  },

  [getterTypes.currentSchoolType]: (state) => {
    return state.schoolType || getItem(currentSchoolType)
  },

  [getterTypes.hasPersonalPage]: (state) => {
    return pipe(
      pathOr([], ['role', 'pages']),
      find(propEq('name', 'Персонал')),
      propOr(false, 'value')
    )(state.currentUser)
  },

  [getterTypes.hasEnrollmentApplicationPage]: (state) => {
    return pipe(
      pathOr([], ['role', 'pages']),
      find(propEq('name', 'Заявка на зарахування')),
      propOr(false, 'value')
    )(state.currentUser)
  },

  [getterTypes.hasTrainersPage]: (state) => {
    return pipe(
      pathOr([], ['role', 'pages']),
      find(propEq('name', 'Тренери')),
      propOr(false, 'value')
    )(state.currentUser)
  },

  [getterTypes.canDecideEnrollment]: (state) => {
    return pipe(
      pathOr([], ['role', 'pages']),
      find(propEq('name', 'Заборона зміни статусу заявок на зарахування')),
      propOr(false, 'value'),
      not
    )(state.currentUser)
  },

  [getterTypes.currentSchoolKindsSport]: (state) => {
    return pathOr([], ['kindsSport'], state.currentSchool)
  },
}

const mutations = {
  [mutationTypes.registerStart](state) {
    state.isSubmitting = true
    state.validationErrors = null
  },

  [mutationTypes.registerSuccess](state, payload) {
    state.isSubmitting = false
    state.currentUser = payload
    state.isLoggedIn = true
  },

  [mutationTypes.registerFailure](state, payload) {
    state.isSubmitting = false
    state.validationErrors = payload
  },

  [mutationTypes.loginStart](state) {
    state.isSubmitting = true
    state.validationErrors = null
  },

  [mutationTypes.loginSuccess](state, payload) {
    state.isSubmitting = false
    state.currentUser = payload
    state.isLoggedIn = true
  },

  [mutationTypes.loginFailure](state, payload) {
    state.isSubmitting = false
    state.validationErrors = payload
  },

  [mutationTypes.getCurrentUserStart](state) {
    state.isLoading = true
  },

  [mutationTypes.getCurrentUserSuccess](state, payload) {
    state.currentUser = {
      ...payload,
      schools: pipe(propOr([], 'schools'), sortBy(prop('type')))(payload),
    }
    state.isLoggedIn = true
    state.isLoading = false
  },

  [mutationTypes.getCurrentUserFailure](state) {
    state.isLoading = false
    state.isLoggedIn = false
    state.currentUser = null
  },

  [mutationTypes.updateCurrentUserStart]() {},
  [mutationTypes.updateCurrentUserFailure]() {},
  [mutationTypes.updateCurrentUserSuccess](state, payload) {
    state.currentUser = payload
  },

  [mutationTypes.resetPasswordStart](state) {
    state.isSubmitting = true
  },
  [mutationTypes.resetPasswordSuccess](state) {
    state.isSubmitting = false
  },
  [mutationTypes.resetPasswordFailure](state, payload) {
    state.validationErrors = payload
    state.isSubmitting = false
  },

  [mutationTypes.setCurrentSchoolId](state, {schoolId, schoolType}) {
    state.schoolId = schoolId
    state.schoolType = schoolType
  },

  [mutationTypes.setCurrentSchool](state, payload) {
    state.currentSchool = payload
  },

  [mutationTypes.logout](state) {
    state.currentUser = null
    state.isLoggedIn = false
  },
}

const actions = {
  [actionType.register](context, credentials) {
    context.commit(mutationTypes.registerStart)
    return new Promise((resolve) => {
      apiAuth
        .register(credentials)
        .then((response) => {
          context.commit(mutationTypes.registerSuccess, response.data.user)
          setItem(nameToken, response.data.user.token)
          resolve(response.data.user)
        })
        .catch((result) => {
          context.commit(
            mutationTypes.registerFailure,
            result.response.data.errors
          )
        })
    })
  },

  [actionType.login](context, {type, credentials}) {
    context.commit(mutationTypes.loginStart)
    return new Promise((resolve, reject) => {
      apiAuth
        .login(type, credentials)
        .then((response) => {
          let token = null
          if (response.data.access_token) {
            token = response.data.access_token
          }

          if (response.data.accessToken) {
            token = response.data.accessToken
          }

          context.commit(mutationTypes.loginSuccess, token)
          setItem(nameToken, token)
          resolve(response.data)
        })
        .catch((result) => {
          const error = result.response ? result.response.data : result
          context.commit(mutationTypes.loginFailure, error)
          reject(result)
        })
    })
  },

  [actionType.getCurrentUser](context) {
    context.commit(mutationTypes.getCurrentUserStart)
    return new Promise((resolve, reject) => {
      apiAuth
        .getCurrentUser()
        .then((response) => {
          context.commit(mutationTypes.getCurrentUserSuccess, response.data)
          resolve(response.data)
        })
        .catch((error) => {
          context.commit(mutationTypes.getCurrentUserFailure)
          reject(error)
        })
    })
  },

  [actionType.updateCurrentUser](context, {currentUserInput}) {
    context.commit(mutationTypes.updateCurrentUserStart)
    return new Promise((resolve) => {
      apiAuth
        .updateCurrentUser(currentUserInput)
        .then((user) => {
          context.commit(mutationTypes.updateCurrentUserSuccess, user)
          resolve(user)
        })
        .catch((result) => {
          context.commit(
            mutationTypes.updateCurrentUserFailure,
            result.response.data.errors
          )
        })
    })
  },

  [actionType.resetPassword](context, {passwords}) {
    context.commit(mutationTypes.resetPasswordStart)
    return new Promise((resolve) => {
      apiAuth
        .resetPassword(passwords)
        .then((user) => {
          context.commit(mutationTypes.resetPasswordSuccess, user)
          resolve(user)
        })
        .catch((result) => {
          context.commit(
            mutationTypes.resetPasswordFailure,
            result.response.data.errors
          )
        })
    })
  },

  [actionType.logout](context) {
    return new Promise((resolve) => {
      setItem(nameToken, '')
      context.commit(mutationTypes.logout)
      resolve()
    })
  },

  [actionType.setCurrentSchoolId](context, {schoolId, schoolType}) {
    return new Promise((resolve) => {
      setItem(currentSchoolId, schoolId)
      setItem(currentSchoolType, schoolType)
      context.commit(mutationTypes.setCurrentSchoolId, {schoolId, schoolType})
      resolve({schoolId, schoolType})
    })
  },
}

export default {
  state,
  mutations,
  actions,
  getters,
}
